/**
 * Cart Sidebar Component
 * Slide-in cart sidebar for quick view
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../../contexts/CartContext';
import CartItem from './CartItem';
import { formatPrice } from '../../utils';
import EmptyState from '../UI/EmptyState';

interface CartSidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

const CartSidebar: React.FC<CartSidebarProps> = ({ isOpen, onClose }) => {
  const { items, itemCount, totalAmount, updateQuantity, removeFromCart } = useCart();

  return (
    <>
      {/* Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <div
        className={`fixed top-0 right-0 h-full w-full max-w-md bg-white shadow-2xl z-50 transform transition-transform duration-300 ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="bg-gradient-to-r from-green-600 to-green-800 text-white px-6 py-4 flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold">Shopping Cart</h2>
              <p className="text-sm text-green-100">{itemCount} {itemCount === 1 ? 'item' : 'items'}</p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:bg-green-700 p-2 rounded-lg transition-all"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Cart Items */}
          <div className="flex-1 overflow-y-auto px-6 py-4">
            {items.length === 0 ? (
              <div className="h-full flex items-center justify-center">
                <div className="text-center">
                  <span className="text-6xl block mb-4">🛒</span>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Your cart is empty</h3>
                  <p className="text-gray-600 mb-6">Add some products to get started!</p>
                  <Link
                    to="/products"
                    onClick={onClose}
                    className="inline-block px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all font-medium"
                  >
                    Browse Products
                  </Link>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                {items.map(item => (
                  <CartItem
                    key={item.product._id}
                    item={item}
                    onUpdateQuantity={updateQuantity}
                    onRemove={removeFromCart}
                    compact
                  />
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          {items.length > 0 && (
            <div className="border-t border-gray-200 px-6 py-4 space-y-4">
              {/* Subtotal */}
              <div className="flex items-center justify-between text-lg">
                <span className="font-medium text-gray-700">Subtotal</span>
                <span className="font-bold text-gray-900">{formatPrice(totalAmount)}</span>
              </div>

              {/* Delivery Info */}
              <p className="text-xs text-gray-500 text-center">
                Delivery fees calculated at checkout
              </p>

              {/* Action Buttons */}
              <div className="space-y-2">
                <Link
                  to="/checkout"
                  onClick={onClose}
                  className="block w-full py-3 px-6 bg-gradient-to-r from-green-600 to-green-800 text-white text-center rounded-lg hover:from-green-700 hover:to-green-900 transition-all font-bold shadow-lg"
                >
                  Proceed to Checkout
                </Link>
                <Link
                  to="/cart"
                  onClick={onClose}
                  className="block w-full py-3 px-6 border-2 border-green-600 text-green-600 text-center rounded-lg hover:bg-green-50 transition-all font-medium"
                >
                  View Full Cart
                </Link>
                <button
                  onClick={onClose}
                  className="block w-full py-2 text-gray-600 hover:text-gray-900 transition-all text-sm"
                >
                  Continue Shopping
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default CartSidebar;

